import { getCountrySalaryConfig } from '../../salary-country.config';

export class CreatePositionAdapter {

  static toServer(data) {
    const normalizedCountry = data.country || null;
    const selectedCities = Array.isArray(data.city) ? data.city : data.city ? [data.city] : [];
    const salaryConfig = getCountrySalaryConfig(normalizedCountry, data.salaryCurrency);

    const validCities = selectedCities.filter(city => !!city);

    const locations = normalizedCountry ? validCities.length > 0 ? validCities.map(city => ({
            country: normalizedCountry,
            city
          })) : [{
            country: normalizedCountry,
            city: null
          }] : [];

    const toReturn = {
      locations,
      jobId: data.jobId || null,
      jobName: data.jobName || null,
     isValidationRequired: data.isValidationRequired !== undefined ? data.isValidationRequired : false,
      jobPositionLevel: data.jobPositionLevel || null,
      employmentType: data.employmentType || null,
      workPlace: data.workPlace || null,
      remoteDaysFromHome: data.remoteDaysFromHome === "" ? null : data.remoteDaysFromHome,
      relocationPossibility: data.relocation ?? null,
      domainsRequired: data.domains && data.domains.length ? data.domains : [],
      domainsStopList: [],
      languages: data.languages && data.languages.length ? data.languages.filter(item => item.name) : [],
      description: data.aboutPosition || null,
      responsibilities: data.responsibilities || null,
      educationDegree: data.educationDegree,
      educationField: data.educationField,
      salary: {
        amountTo: this.normalizeSalaryAmount(data.salaryAmountTo),
        amount: this.normalizeSalaryAmount(data.salaryAmount),
        currency: salaryConfig.currency,
        period: data.salaryPeriod || null
      }
    };
    return toReturn;
  }

  static toStore(data) {
    const locations = Array.isArray(data.locations) && data.locations.length
      ? data.locations : data.location ? [data.location] : [];

    const country = locations.find(item => !!item?.country)?.country || null;
    const city = locations.map(item => item?.city).filter(item => !!item);

    const toReturn = {
      jobPosition: data.jobPosition || null,
      jobPositions: data.jobPositions || [],
      hardSkills: data.hardSkills || null,
      softSkills: data.softSkills || null,
      descriptionPrompt: data.descriptionPrompt || null,
      status: data.status || null,
      isValidationRequired: data.isValidationRequired ?? false,
      jobPositionLevel: data.jobPositionLevel || null,
      assessments: data.assessments || [],
      forceHighAccuracy: data.forceHighAccuracy ?? false,
      additionalInfo: {
        jobId: data.jobId || null,
        jobName: data.jobName || null,
        salaryPeriod: data.salary && data.salary.period ? data.salary.period : null,
        salaryCurrency: data.salary && data.salary.currency ? data.salary.currency : 'USD',
        salaryAmount: data.salary?.amount ?? null,
        salaryAmountTo: data.salary?.amountTo ?? null,
        country,
        city,
        workPlace: data.workPlace || null,
        remoteDaysFromHome: data.remoteDaysFromHome ?? null,
        employmentType: data.employmentType || null,
        relocation: data.relocationPossibility ?? null,
        domains: data.domainsRequired && data.domainsRequired.length ? data.domainsRequired : [],
        educationDegree: data.educationDegree || null,
        educationField: data.educationField || null,
        languages: data.languages || [{ name: '', level: ''}],
        aboutPosition: data.description || null,
        responsibilities: data.responsibilities || null,
      }
    }
    return toReturn;
  }

  private static normalizeSalaryAmount(value): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const integerPart = String(value).replace(/,/g, '').split('.')[0]; // used to remove decimals and commas
    const digitsOnly = integerPart.replace(/\D/g, ''); // used to remove non-digits

    return digitsOnly ? Number(digitsOnly) : null;
  }

}

export class CreatePositionSkillsAdapter {

  static toViewMerge(preset, userSkills) {
    if (preset && preset.length) {
      const clearSkillsAdds = { isRequired: false, experience: 0, years: 0 , isValidationRequired : false};
      const prefilledTree = preset.map(branch => {
        if (!userSkills) { 
          return { ...branch, skills: branch.skills.map(skill => ({ ...skill, ...clearSkillsAdds })) }; 
        }
        const canMergeBranch = userSkills.find(b => b.id === branch.id);
        if (canMergeBranch) {
          const skillsToAdd = branch.skills.filter(skill => !canMergeBranch.skills.find(s => s.id === skill.id))
            .map(val => ({ id: val.id, name: val.name, ...clearSkillsAdds }));
          return { ...branch, skills: [ ...canMergeBranch.skills, ...skillsToAdd ]};
        } else {
          return { ...branch, skills: branch.skills.map(val => ({ id: val.id, name: val.name, ...clearSkillsAdds })) };
        }
      });
      
      if (!userSkills) { return prefilledTree; }
      const branchesToAdd = userSkills.filter(branch => !prefilledTree.find(b => b.id === branch.id));
      return [ ...prefilledTree, ...branchesToAdd ];
    }
    return null;
  }

  static toViewMergeDepricated(branches, skills) {
    const selectedSkills = [];
    if (skills && skills.length) {
      skills.map(branch => {
        branch.skills.map(skill => selectedSkills.push(skill))
      })
    }
    if (branches && branches.length) {
      const skillTree = branches.map(branch => ({
        id: branch.id,
        name: branch.name,
        skills: branch.skills.map(skill => {
          const selected = selectedSkills.find(item => item.id === skill.id);
          return {
            id: skill.id,
            name: skill.name,
            isRequired: selected ? selected.isRequired : false,
            experience: selected ? selected.experience : 0,
            years: selected ? selected.years : 0,
            isValidationRequired : selected ? selected.isValidationRequired : false
          }
        })
      }))
      return skillTree;
    }
    return null;
  }

  static toView(branches) {
    if (branches && branches.length) {
      const toReturn = branches.map(branch => ({
        id: branch.id,
        name: branch.name,
        skills: branch.skills.map(skill => ({
          id: skill.id,
          name: skill.name,
          isRequired: false,
          isValidationRequired: false,
          experience: 0,
          years: 0
        }))
      }))
      return toReturn;
    }
    return null;
  }

  static toServer(skills) {
    if (skills) {
      const toReturn = skills.reduce((filtered, item) => {
        if (item.skills && item.skills.length && item.skills.some(val => val.experience || val.years || val.isRequired || val.isValidationRequired)) {
          filtered.push({ ...item, skills: item.skills.filter((item: any) => item.experience) });
        }
        return filtered;
      }, [])
      return toReturn;
    }
    return [];
  }
  
}
